// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */

import { store } from 'redux/store';
import { AxiosConfig, createAxios } from 'ts/createAxios';
import { getApiPath } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import {
  Area,
  AreaDetail,
  AreaGroup,
  CollectDelivery,
  CollectDeliveryResponse,
} from 'util/Types';

export const getCollectSiteGroups = async (params?: {
  keyword?: string;
  successCallback?: (data: (Area | AreaGroup)[]) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
  groupSiteId?: string;
  axiosConfigs?: AxiosConfig;
  showError?: boolean;
}) => {
  const {
    keyword,
    successCallback,
    groupSiteId,
    failedCallback,
    finallyCallback,
    axiosConfigs,
    showError = true,
  } = params || {};

  try {
    const { data } = await createAxios(axiosConfigs).get<(Area | AreaGroup)[]>(
      API_ENDPOINTS.COLLECT_GROUP_SITES,
      {
        params: {
          keyword,
          groupSiteId,
        },
      }
    );

    successCallback?.(data);
  } catch (e) {
    if (showError) {
      handleError(e);
    }

    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectAreas = async (params?: {
  keyword?: string;
  successCallback?: (data: Area[]) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
  axiosConfigs?: AxiosConfig;
  showError?: boolean;
}) => {
  const {
    keyword,
    successCallback,
    failedCallback,
    finallyCallback,
    axiosConfigs,
    showError = true,
  } = params || {};

  try {
    const { data } = await createAxios(axiosConfigs).get<Area[]>(
      API_ENDPOINTS.COLLECT_AREAS,
      {
        params: {
          keyword,
        },
      }
    );

    successCallback?.(data);
  } catch (e) {
    if (showError) {
      handleError(e);
    }

    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectAreaDetail = async (params: {
  areaId: string;
  successCallback?: (data: AreaDetail) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
}) => {
  const { areaId, successCallback, failedCallback, finallyCallback } = params;

  try {
    const { data } = await createAxios({
      prefix: 'v2',
    }).get<AreaDetail>(
      getApiPath(API_ENDPOINTS.COLLECT_AREA_DETAIL, {
        id: areaId,
      })
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectDeliveries = async (params: {
  deliveryIds: string[];
  cursor: string | null;
  take?: number;
  successCallback?: (data: CollectDelivery[]) => void;
  failedCallback?: (error: any) => void;
  getAll?: boolean;
  showLoading?: boolean;
  type?: 'get' | 'globalExport';
}) => {
  const {
    deliveryIds,
    cursor,
    take = 500,
    successCallback,
    failedCallback,
    getAll = true,
    showLoading = true,
    type = 'get',
  } = params;

  try {
    const {
      data: { nextCursor, deliveryDetails },
    } = await createAxios({
      prefix: 'v2',
      notShowLoading: !showLoading,
    }).post<CollectDeliveryResponse>(
      type === 'get'
        ? API_ENDPOINTS.COLLECT_DELIVERIES
        : API_ENDPOINTS.COLLECT_STREAM_DELIVERIES,
      type === 'get'
        ? {
            deliveryIds,
            cursor,
            take,
          }
        : {
            deliveryIds,
          }
    );

    if (
      getAll &&
      nextCursor &&
      (type !== 'globalExport' ||
        store.getState().historiesCsvExport.isExporting)
    ) {
      await getCollectDeliveries({
        deliveryIds,
        cursor: nextCursor,
        take,
        successCallback: (data) => {
          successCallback?.([...deliveryDetails, ...data]);
        },
        failedCallback,
        getAll,
        showLoading,
        type,
      });
    } else if (
      type !== 'globalExport' ||
      store.getState().historiesCsvExport.isExporting
    ) {
      successCallback?.(deliveryDetails);
    }
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};
